import * as yup from 'yup';
import {
  CampaignFilterOperator,
  CampaignMedium,
  MarketingBusinesses,
  MarketingCampaignFilters,
  MarketingCampaigns,
  MarketingCampaignSegments,
  MarketingMediumTemplates,
  ServiceCategory,
} from '@prisma/client';
import {
  PocketflowsBusinessSegmentUsage,
  PocketflowsCampaign,
  PocketflowsEmailTemplate,
  PocketflowsSmsTemplate,
} from './pocketflows';
import {
  isEmailTemplateValid,
  isSmsTemplateValid,
} from '../../helpers/marketing';

export interface GetMarketingDataParams {
  businessId: string;
  includeCampaigns?: boolean;
  create?: boolean;
  includeSenders?: boolean;
  includeCreditsUsage?: boolean;
}

export interface MarketingCampaignSegmentWithFilters
  extends MarketingCampaignSegments {
  filters: MarketingCampaignFilters[];
}

export interface MarketingCampaignWithSource extends MarketingCampaigns {
  source: PocketflowsCampaign;
  templates: MarketingMediumTemplates[];
}

export interface PocketflowsEmailAddress {
  id: string;
  email_address: string;
  display_name: string;
}

export interface PocketflowsPhoneNumber {
  id: string;
  phone_number: string;
}

export interface MarketingBusinessesWithCampaigns extends MarketingBusinesses {
  campaigns?: MarketingCampaignWithSource[];
  emailAddresses?: PocketflowsEmailAddress[];
  phoneNumbers?: PocketflowsPhoneNumber[];
  creditsUsage?: PocketflowsBusinessSegmentUsage;
}

export interface MarketingEmailTemplateWithSource
  extends MarketingMediumTemplates {
  source: PocketflowsEmailTemplate;
  inUse: boolean;
}

export interface MarketingSmsTemplateWithSource
  extends MarketingMediumTemplates {
  source: PocketflowsSmsTemplate;
  inUse: boolean;
}

export interface SegmentCustomer {
  customerId: string;
  user: {
    firstName: string | undefined;
    lastName: string | undefined;
    phoneNumber: string;
    email: string | undefined;
  };
}

const INVALID_FILTER_CATEGORY_ERROR = 'One or more filters are invalid';

export const filterOperatorSchema = yup
  .mixed<CampaignFilterOperator>()
  .oneOf(['LT', 'GT']);

export const spendFilterSchema = yup.object({
  category: yup.mixed<'SPEND'>().oneOf(['SPEND']).required(),
  value: yup.number().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const appointmentDateFilterSchema = yup.object({
  category: yup
    .mixed<'LAST_APPOINTMENT_DATE'>()
    .oneOf(['LAST_APPOINTMENT_DATE'])
    .required(),
  value: yup.string().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const appointmentCountFilterSchema = yup.object({
  category: yup
    .mixed<'APPOINTMENT_COUNT'>()
    .oneOf(['APPOINTMENT_COUNT'])
    .required(),
  value: yup.number().integer().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const appointmentCreatedFilterSchema = yup.object({
  category: yup
    .mixed<'APPOINTMENT_CREATED'>()
    .oneOf(['APPOINTMENT_CREATED'])
    .required(),
  value: yup.string().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const appointmentWithinDaysFilterSchema = yup.object({
  category: yup
    .mixed<'LAST_APPOINTMENT_WITHIN_DAYS'>()
    .oneOf(['LAST_APPOINTMENT_WITHIN_DAYS'])
    .required(),
  value: yup.number().integer().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const nextAppointmentDateFilterSchema = yup.object({
  category: yup
    .mixed<'NEXT_APPOINTMENT_DATE'>()
    .oneOf(['NEXT_APPOINTMENT_DATE'])
    .required(),
  value: yup.string().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR),
});

export const employeeRelationFilterSchema = yup.object({
  category: yup
    .mixed<'EMPLOYEE_RELATION'>()
    .oneOf(['EMPLOYEE_RELATION'])
    .required(),
  value: yup.string().required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR), // not used - keep for types
});

export const appointmentServiceFilterSchema = yup.object({
  category: yup
    .mixed<'APPOINTMENT_SERVICE'>()
    .oneOf(['APPOINTMENT_SERVICE'])
    .required(),
  value: yup
    .mixed<ServiceCategory>()
    .oneOf(Object.values(ServiceCategory))
    .required(INVALID_FILTER_CATEGORY_ERROR),
  operator: filterOperatorSchema.required(INVALID_FILTER_CATEGORY_ERROR), // not used - keep for types
});

export type SpendFilter = yup.InferType<typeof spendFilterSchema>;
export type AppointmentDateFilter = yup.InferType<
  typeof appointmentDateFilterSchema
>;
export type AppointmentCountFilter = yup.InferType<
  typeof appointmentCountFilterSchema
>;
export type AppointmentCreatedFilter = yup.InferType<
  typeof appointmentCreatedFilterSchema
>;
export type AppointmentWithinDaysFilter = yup.InferType<
  typeof appointmentWithinDaysFilterSchema
>;
export type NextAppointmentDateFilter = yup.InferType<
  typeof nextAppointmentDateFilterSchema
>;

export type EmployeeRelationFilter = yup.InferType<
  typeof employeeRelationFilterSchema
>;

export type AppointmentServiceFilter = yup.InferType<
  typeof appointmentServiceFilterSchema
>;

export type CampaignFilter =
  | SpendFilter
  | AppointmentDateFilter
  | AppointmentCountFilter
  | AppointmentCreatedFilter
  | AppointmentWithinDaysFilter
  | NextAppointmentDateFilter
  | EmployeeRelationFilter
  | AppointmentServiceFilter;

const SEND_REQUIRED_ERROR = 'Please select a send time';
const INVALID_SEND_TYPE_ERROR = 'Invalid send time type';

export const sendNowSchema = yup.object({
  type: yup
    .mixed<'NOW'>()
    .oneOf(['NOW'], INVALID_SEND_TYPE_ERROR)
    .required(SEND_REQUIRED_ERROR),
});

const SELECT_DATE_ERROR =
  'Please select a date and time for your campaign to be sent at';

export const sendAtSchema = yup.object({
  type: yup
    .mixed<'SCHEDULED'>()
    .oneOf(['SCHEDULED'], INVALID_SEND_TYPE_ERROR)
    .required(SEND_REQUIRED_ERROR),
  dateTime: yup
    .date()
    .typeError(SELECT_DATE_ERROR)
    .test({
      test: value => {
        const dateValue = value ? new Date(value) : null;
        if (!dateValue || Number.isNaN(dateValue.getTime())) return false;
        return !!value && value > new Date();
      },
      message: 'Send time must be in the future',
    })
    .required(SELECT_DATE_ERROR),
});

export type SendNow = yup.InferType<typeof sendNowSchema>;
export type SendAt = yup.InferType<typeof sendAtSchema>;

export const campaignSegmentFilterSchema = yup.lazy((value: CampaignFilter) => {
  switch (value?.category) {
    case 'SPEND':
      return spendFilterSchema;
    case 'LAST_APPOINTMENT_DATE':
      return appointmentDateFilterSchema;
    case 'APPOINTMENT_CREATED':
      return appointmentCreatedFilterSchema;
    case 'APPOINTMENT_COUNT':
      return appointmentCountFilterSchema;
    case 'LAST_APPOINTMENT_WITHIN_DAYS':
      return appointmentWithinDaysFilterSchema;
    case 'NEXT_APPOINTMENT_DATE':
      return nextAppointmentDateFilterSchema;
    case 'EMPLOYEE_RELATION':
      return employeeRelationFilterSchema;
    case 'APPOINTMENT_SERVICE':
      return appointmentServiceFilterSchema;
    default:
      // throw error if invalid category
      return yup
        .mixed()
        .test('invalid-category', INVALID_FILTER_CATEGORY_ERROR, () => false);
  }
});

export const sendTimeSchema = yup.lazy((value: SendNow | SendAt) => {
  switch (value?.type) {
    case 'NOW':
      return sendNowSchema;
    case 'SCHEDULED':
      return sendAtSchema;
    default:
      return yup
        .mixed()
        .test('invalid-type', INVALID_SEND_TYPE_ERROR, () => false);
  }
});

export const sendTimeSchemaOptional = yup.lazy((value: SendNow | SendAt) => {
  if (!value || !Object.keys(value).length) return yup.mixed().nullable();
  return sendTimeSchema;
});

export type SendTime = yup.InferType<typeof sendTimeSchema>;

const VALID_TEMPLATE_ERROR = 'Please select a valid template';
const TEMPLATE_REQUIRED_ERROR = 'Please select a template';

export const smsContentSchema = yup.object({
  smsTemplateId: yup
    .number()
    .integer(VALID_TEMPLATE_ERROR)
    .required(TEMPLATE_REQUIRED_ERROR),
});

export const emailContentSchema = yup.object({
  emailTemplateId: yup
    .number()
    .integer(VALID_TEMPLATE_ERROR)
    .required(TEMPLATE_REQUIRED_ERROR),
});

export type SmsContentSchema = yup.InferType<typeof smsContentSchema>;
export type EmailContentSchema = yup.InferType<typeof emailContentSchema>;
export type ContentSchema = SmsContentSchema | EmailContentSchema;

export const conditionSchema = yup
  .mixed<'ALL' | 'ANY'>()
  .oneOf(['ALL', 'ANY'], 'Please select a valid condition for your segment');

export type Condition = yup.InferType<typeof conditionSchema>;

export const campaignSegmentSchema = yup.object({
  id: yup.mixed<string | number>().nullable().optional(),
  condition: conditionSchema.required('Condition is required in a segment'),
  filters: yup
    .array(campaignSegmentFilterSchema)
    .required('Filters are required in a segment'),
  topPercentage: yup.number().integer().min(0).max(100).optional(),
  name: yup.string().min(1).nullable().optional(),
});

export type CampaignSegment = yup.InferType<typeof campaignSegmentSchema>;

const SEGMENT_NAME_ERROR = 'Please enter a valid name for your segment';

export const finalCampaignSegmentSchema = campaignSegmentSchema.concat(
  yup.object({
    name: yup.string().min(1, SEGMENT_NAME_ERROR).required(SEGMENT_NAME_ERROR),
  }),
);

export type FinalCampaignSegment = yup.InferType<
  typeof finalCampaignSegmentSchema
>;

const MEDIUM_ERROR = 'Please select a valid medium';
export const campaignMediumSchema = yup
  .mixed<CampaignMedium>()
  .oneOf(Object.values(CampaignMedium), MEDIUM_ERROR);

export const upsertDraftCampaignSchema = yup.object({
  id: yup.string().nullable().optional(),
  medium: campaignMediumSchema.required(MEDIUM_ERROR),
  name: yup
    .string()
    .min(1, 'Please enter a valid name for your campaign')
    .required(),
  includeSegment: campaignSegmentSchema.nullable(),
  excludeSegment: campaignSegmentSchema.nullable(),
  content: yup
    .object<SmsContentSchema | EmailContentSchema>()
    .nullable()
    .optional()
    .when('medium', {
      is: 'SMS',
      then: () => smsContentSchema.nullable().optional(),
      otherwise: () => emailContentSchema.nullable().optional(),
    }),
  sendTime: sendTimeSchemaOptional,
  emailAddressId: yup.string().nullable().optional(),
});

export const upsertCampaignSchema = upsertDraftCampaignSchema.concat(
  yup.object({
    sendTime: sendTimeSchema,
    emailAddressId: yup.string().nullable().optional(),
    content: yup
      .object<SmsContentSchema | EmailContentSchema>()
      .when('medium', {
        is: 'SMS',
        then: () => smsContentSchema,
        otherwise: () => emailContentSchema,
      })
      .required(VALID_TEMPLATE_ERROR),
  }),
);

export const getUpsertCampaignSchema = (
  emailTemplates: MarketingEmailTemplateWithSource[],
  smsTemplates: MarketingSmsTemplateWithSource[],
  draft?: boolean,
) =>
  (draft ? upsertDraftCampaignSchema : upsertCampaignSchema).test({
    test: (values, ctx) => {
      try {
        if (!values.medium) throw new Error('Invalid');
        if (draft) return true;
        if (values.medium === 'SMS') {
          const smsTemplate = smsTemplates?.find(
            template =>
              template.id ===
              (values.content as SmsContentSchema).smsTemplateId,
          );
          if (!smsTemplate) throw new Error('Invalid');
          const isValid =
            !!smsTemplate && isSmsTemplateValid(smsTemplate.source);
          if (!isValid) throw new Error('Invalid');
          return true;
        }
        const emailTemplate = emailTemplates?.find(
          template =>
            template.id ===
            (values.content as EmailContentSchema).emailTemplateId,
        );
        const isValid =
          !!emailTemplate && isEmailTemplateValid(emailTemplate.source);
        if (!isValid) throw new Error('Invalid');
        return true;
      } catch {
        return ctx.createError({
          path: 'content',
          message: VALID_TEMPLATE_ERROR,
        });
      }
    },
    message: 'Invalid template',
  });

export type CreateUpdateCampaignPayload = yup.InferType<
  typeof upsertCampaignSchema
>;

export type CreateUpdateDraftCampaignPayload = yup.InferType<
  typeof upsertDraftCampaignSchema
>;

export type UpsertCampaignPayload = {
  id?: CreateUpdateCampaignPayload['id'];
  name: CreateUpdateCampaignPayload['name'];
  medium: CreateUpdateCampaignPayload['medium'];
  sendTime: CreateUpdateDraftCampaignPayload['sendTime'];
  templateId: number | null;
  includeSegmentId: number | null;
  prebuiltIncludeSegmentId: string | null;
  includeSegment: CreateUpdateCampaignPayload['includeSegment'];
  excludeSegmentId: number | null;
  prebuiltExcludeSegmentId: string | null;
  excludeSegment: CreateUpdateCampaignPayload['excludeSegment'];
  isFinal: boolean;
  emailAddressId?: string | null;
};

export interface CampaignCost {
  costPerSegment: Record<CampaignMedium, number>;
  monthlyCredits: Record<CampaignMedium, number>;
  segmentUsage: PocketflowsBusinessSegmentUsage;
  creditsRemaining: number;
  estimatedCredits: Record<CampaignMedium, number>;
  overage: number;
  requirePayment: boolean;
  estimatedCampaignCost: number;
  creditsCharged: Record<CampaignMedium, number>;
  creditsSurplus: Record<CampaignMedium, number>;
}

export * from './pocketflows';
